import { getSettings, updateSettings } from "./apiservices/settings";
import { getPlanType, packageAccess } from "./helpers";

const DEFAULT_WORK_WEEK = ["monday", "tuesday", "wednesday", "thursday", "friday"];

const getTimestampValue = (timestamp) => {
  if (!timestamp) return 0;
  if (typeof timestamp === "number") return timestamp;
  if (timestamp?.seconds) return timestamp.seconds;
  return 0;
};

const getLatestActiveSetting = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return [...items]
    .filter((item) => !item?.deleted)
    .sort((a, b) => {
      const aUpdated = getTimestampValue(a?.updatedAt);
      const bUpdated = getTimestampValue(b?.updatedAt);
      if (aUpdated !== bUpdated) return bUpdated - aUpdated;
      const aCreated = getTimestampValue(a?.createdAt);
      const bCreated = getTimestampValue(b?.createdAt);
      return bCreated - aCreated;
    })[0];
};

const getSettingsDoc = async (collectionName, companyId) =>
  new Promise((resolve) => {
    getSettings(collectionName, companyId, (res) => {
      resolve(getLatestActiveSetting(res?.data || []));
    });
  });

const arraysEqual = (a = [], b = []) => a.join(",") === b.join(",");

const PLAN_RANK = Object.freeze({
  freemium: 0,
  launch: 1,
  growth: 2,
  scale: 3,
  other: 99,
});

const getPlanRank = (planId) => PLAN_RANK[getPlanType(planId)] ?? PLAN_RANK.other;

export async function enforcePlanDowngradeSettings({
  companyId,
  previousPlanId,
  planId,
  planAccess,
}) {
  if (!companyId || !planId) return;
  const isDirectionalChangeKnown = Boolean(previousPlanId);
  const isDowngrade =
    isDirectionalChangeKnown && getPlanRank(planId) < getPlanRank(previousPlanId);
  if (isDirectionalChangeKnown && !isDowngrade) {
    // Upgrade/lateral change: intentionally do not strip settings.
    return;
  }

  const planType = getPlanType(planId);
  const isLaunchOrFreemium = planType === "launch" || planType === "freemium";
  const isFreemium = planType === "freemium";
  const permissions = await packageAccess({ prdId: planId, planAccess });
  const smsAllowed = Number(permissions?.messageLimits || 0) > 0;
  const allowAutoReports = Boolean(
    planAccess?.features?.client_manager?.auto_reports
  );
  const allowClothingRules = Boolean(
    planAccess?.features?.supply_manager?.clothing_rules
  );

  const [
    reportSettings,
    notificationSettings,
    suppliesSettings,
    issueSettings,
    appointmentSettings,
  ] = await Promise.all([
    getSettingsDoc("report_settings", companyId),
    getSettingsDoc("notification_settings", companyId),
    getSettingsDoc("supplie_settings", companyId),
    getSettingsDoc("issue_settings", companyId),
    getSettingsDoc("appointment_settings", companyId),
  ]);

  const updates = [];
  const planChangedSettingsMeta = {
    changedByType: "plan_change_settings_sync",
    changedAtClient: Date.now(),
  };

  if (reportSettings?.id) {
    const next = {
      auto_shift_reports_enabled: allowAutoReports
        ? Boolean(reportSettings?.auto_shift_reports_enabled)
        : false,
      client_delivered_reports:
        allowAutoReports && Array.isArray(reportSettings?.client_delivered_reports)
          ? reportSettings.client_delivered_reports.slice(0, 1)
          : [],
      planChangedSettings: planChangedSettingsMeta,
      companyId,
    };
    const hasChanges =
      reportSettings?.auto_shift_reports_enabled !== next.auto_shift_reports_enabled ||
      !arraysEqual(
        reportSettings?.client_delivered_reports || [],
        next.client_delivered_reports
      );
    if (hasChanges) {
      updates.push(updateSettings(next, "report_settings", reportSettings.id));
    }
  }

  if (notificationSettings?.id && !smsAllowed) {
    const currentSmsRoles = notificationSettings?.smsRoles || [];
    if (currentSmsRoles.length > 0) {
      updates.push(
        updateSettings(
          {
            smsRoles: [],
            planChangedSettings: planChangedSettingsMeta,
            companyId,
          },
          "notification_settings",
          notificationSettings.id
        )
      );
    }
  }

  if (suppliesSettings?.id && !allowClothingRules) {
    const interval = suppliesSettings?.intervalTimeOfClothing || {};
    const hasRestrictedInterval =
      Number(interval?.days || 0) > 0 ||
      Number(interval?.months || 0) > 0 ||
      Number(interval?.years || 0) > 0;
    if (hasRestrictedInterval) {
      updates.push(
        updateSettings(
          {
            intervalTimeOfClothing: { days: 0, months: 0, years: 0 },
            planChangedSettings: planChangedSettingsMeta,
            companyId,
          },
          "supplie_settings",
          suppliesSettings.id
        )
      );
    }
  }

  if (issueSettings?.id && isLaunchOrFreemium && issueSettings?.publicPageType === "Yes") {
    updates.push(
      updateSettings(
        {
          publicPageType: "No",
          planChangedSettings: planChangedSettingsMeta,
          companyId,
        },
        "issue_settings",
        issueSettings.id
      )
    );
  }

  if (appointmentSettings?.id) {
    const nextAppointment = {
      earlyCheckIn: isLaunchOrFreemium ? "15" : appointmentSettings?.earlyCheckIn || "15",
      endShiftLateThreshold: isLaunchOrFreemium
        ? "15"
        : appointmentSettings?.endShiftLateThreshold || "15",
      geoFencingStatus: isLaunchOrFreemium
        ? "Disabled"
        : appointmentSettings?.geoFencingStatus || "Disabled",
      measurementUnit: isLaunchOrFreemium
        ? "meters"
        : appointmentSettings?.measurementUnit || "meters",
      geoFencingRadius: isLaunchOrFreemium ? "" : appointmentSettings?.geoFencingRadius || "",
      workWeek: isFreemium
        ? DEFAULT_WORK_WEEK
        : appointmentSettings?.workWeek?.length
          ? appointmentSettings.workWeek
          : DEFAULT_WORK_WEEK,
      startTime: isFreemium ? "09:00 am" : appointmentSettings?.startTime || "09:00 am",
      endTime: isFreemium ? "07:00 pm" : appointmentSettings?.endTime || "07:00 pm",
      standardWorkTime: isFreemium ? "no" : appointmentSettings?.standardWorkTime || "no",
      timeInterval: isFreemium ? "30 min" : appointmentSettings?.timeInterval || "30 min",
      breakTooLongThreshold: isFreemium
        ? "30"
        : appointmentSettings?.breakTooLongThreshold || "30",
      planChangedSettings: planChangedSettingsMeta,
      companyId,
    };
    const hasAppointmentChanges =
      (appointmentSettings?.earlyCheckIn || "15") !== nextAppointment.earlyCheckIn ||
      (appointmentSettings?.endShiftLateThreshold || "15") !==
        nextAppointment.endShiftLateThreshold ||
      (appointmentSettings?.geoFencingStatus || "Disabled") !==
        nextAppointment.geoFencingStatus ||
      (appointmentSettings?.measurementUnit || "meters") !==
        nextAppointment.measurementUnit ||
      (appointmentSettings?.geoFencingRadius || "") !== nextAppointment.geoFencingRadius ||
      !arraysEqual(
        appointmentSettings?.workWeek?.length ? appointmentSettings.workWeek : DEFAULT_WORK_WEEK,
        nextAppointment.workWeek
      ) ||
      (appointmentSettings?.startTime || "09:00 am") !== nextAppointment.startTime ||
      (appointmentSettings?.endTime || "07:00 pm") !== nextAppointment.endTime ||
      (appointmentSettings?.standardWorkTime || "no") !== nextAppointment.standardWorkTime ||
      (appointmentSettings?.timeInterval || "30 min") !== nextAppointment.timeInterval ||
      (appointmentSettings?.breakTooLongThreshold || "30") !==
        nextAppointment.breakTooLongThreshold;

    if (hasAppointmentChanges) {
      updates.push(
        updateSettings(nextAppointment, "appointment_settings", appointmentSettings.id)
      );
    }
  }

  if (updates.length > 0) {
    await Promise.allSettled(updates);
  }
}
